/**
 * Product feed / repeat block (K4 part 3, Editor 2.0).
 *
 * A block that repeats over a list of shop products is written in the mail as
 * the plain-text directive
 *
 *   {{np_products source=newest count=3 columns=3 label=Shop%20now}}
 *
 * and replaced by an email-safe table of product cards in ONE place per mail
 * version, together with shared blocks: when a campaign is prepared (frozen
 * into the snapshot), when a flow step sends, and in the previews. The
 * queue consumer never expands (the snapshot is already expanded), so a send
 * batch costs no extra reads, and every recipient of a campaign sees the
 * same products and prices that were approved.
 *
 * Sources (all the same for every recipient; contact-specific sources such
 * as the abandoned cart or the last order are deliberately not offered):
 *
 *   newest   the shop's newest products (the order the shop plugin returns)
 *   best     best sellers, from the most recent order lines
 *   picked   a hand-picked list: ids=12,34,56
 *
 * Settings (all optional, strict: anything unknown is an error, never ignored):
 *
 *   count=1..6      how many products (default 3; for `picked` the id count)
 *   columns=1..3    cards per row (default 3; never more than there are cards)
 *   label=...       button text, percent-encoded (default "View product")
 *   lang=de         language for the price format (default en)
 *   ids=1,2,3       the picked products (only with source=picked)
 *
 * The characters are limited to ones that pass the HTML sanitiser, Handlebars
 * and link rewriting unchanged. A product that is out of stock is left out.
 * The shop's prices are shown as the shop reported them at the last refresh.
 *
 * This file is pure (no database) so the admin app can use it for its live
 * preview.
 */

// Curly braces are dropped, not encoded: the sanitiser decodes entities, so a
// shop product named `{{...}}` would otherwise reach the merge-tag renderer.
function escapeHtml(s: string): string {
  return s
    .replace(/[{}]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export const MAX_PRODUCT_FEEDS_PER_MAIL = 4;
export const MAX_FEED_COUNT = 6;
export const DEFAULT_FEED_COUNT = 3;
export const MAX_FEED_COLUMNS = 3;
export const DEFAULT_FEED_LABEL = 'View product';
export const MAX_FEED_LABEL_LENGTH = 30;

export type FeedSource = 'newest' | 'best' | 'picked';
const SOURCES: readonly FeedSource[] = ['newest', 'best', 'picked'];

export interface ProductFeed {
  readonly source: FeedSource;
  readonly count: number;
  readonly columns: number;
  readonly label: string;
  readonly lang: string;
  /** Only for `picked`. */
  readonly ids: readonly string[];
}

/** One product as the catalogue holds it (`catalog_products`). */
export interface CatalogProduct {
  readonly id: string;
  readonly name: string;
  /** Plain decimal strings ("29.00"); "" when the shop has none. */
  readonly price: string;
  readonly regularPrice: string;
  readonly salePrice: string;
  readonly currency: string;
  readonly imageUrl: string | null;
  readonly permalink: string;
  readonly stockStatus: 'instock' | 'outofstock' | 'onbackorder';
  /** 0 = newest. */
  readonly shopRank: number;
  /** Units on the most recent order lines (0 = none). */
  readonly soldRecent: number;
}

/** Colours the cards use: the tenant's own brand kit, with a safe fallback. */
export interface FeedStyle {
  readonly primaryColor: string;
  readonly textColor: string;
}

export const DEFAULT_FEED_STYLE: FeedStyle = { primaryColor: '#5457D6', textColor: '#18181B' };

const DIRECTIVE = /\{\{np_products((?: [a-z]+=[A-Za-z0-9%_.,~-]*)*)\}\}/g;
const VALUE = /^[A-Za-z0-9%_.,~-]*$/;
const ID = /^\d{1,12}$/;

/** True when `source` mentions a product feed directive at all (a cheap check before any work). */
export function hasProductFeeds(source: string): boolean {
  return source.includes('{{np_products');
}

/** Number of `{{np_products` occurrences that are not a well-formed directive. */
export function malformedProductFeedRefs(source: string): number {
  if (!hasProductFeeds(source)) return 0;
  return source.split('{{np_products').length - 1 - [...source.matchAll(DIRECTIVE)].length;
}

export type ParseFeedResult =
  | { readonly ok: true; readonly feed: ProductFeed }
  | { readonly ok: false; readonly error: string };

function decodeLabel(raw: string): string | undefined {
  try {
    return decodeURIComponent(raw);
  } catch {
    return undefined;
  }
}

/** Parses the settings text after `{{np_products`. */
export function parseProductFeed(settings: string): ParseFeedResult {
  const parts = settings.trim().split(/\s+/).filter(Boolean);
  const seen = new Set<string>();
  let source: FeedSource = 'newest';
  let count: number | undefined;
  let columns = MAX_FEED_COLUMNS;
  let label = DEFAULT_FEED_LABEL;
  let lang = 'en';
  let ids: string[] = [];
  for (const part of parts) {
    const eq = part.indexOf('=');
    const key = eq < 0 ? part : part.slice(0, eq);
    const value = eq < 0 ? '' : part.slice(eq + 1);
    if (eq < 0 || !VALUE.test(value)) {
      return { ok: false, error: `"${part.slice(0, 30)}" is not a setting (use key=value).` };
    }
    if (seen.has(key)) return { ok: false, error: `"${key}" is set twice.` };
    seen.add(key);
    switch (key) {
      case 'source': {
        if (!(SOURCES as readonly string[]).includes(value)) {
          return { ok: false, error: 'source must be newest, best or picked.' };
        }
        source = value as FeedSource;
        break;
      }
      case 'count': {
        const n = Number(value);
        if (!/^\d{1,2}$/.test(value) || n < 1 || n > MAX_FEED_COUNT) {
          return { ok: false, error: `count must be 1 to ${MAX_FEED_COUNT}.` };
        }
        count = n;
        break;
      }
      case 'columns': {
        const n = Number(value);
        if (!/^\d$/.test(value) || n < 1 || n > MAX_FEED_COLUMNS) {
          return { ok: false, error: `columns must be 1 to ${MAX_FEED_COLUMNS}.` };
        }
        columns = n;
        break;
      }
      case 'label': {
        const decoded = decodeLabel(value);
        if (
          decoded === undefined ||
          decoded.trim() === '' ||
          decoded.length > MAX_FEED_LABEL_LENGTH ||
          /[<>{}"\\]/.test(decoded)
        ) {
          return {
            ok: false,
            error: `The button text must be 1 to ${MAX_FEED_LABEL_LENGTH} characters, without < > { } or quotes.`,
          };
        }
        label = decoded.trim();
        break;
      }
      case 'lang': {
        if (!/^[a-z]{2}$/.test(value)) return { ok: false, error: 'lang must be two letters.' };
        lang = value;
        break;
      }
      case 'ids': {
        const list = value.split(',');
        if (!list.every((id) => ID.test(id)) || list.length > MAX_FEED_COUNT) {
          return {
            ok: false,
            error: `ids must be 1 to ${MAX_FEED_COUNT} product numbers separated by commas.`,
          };
        }
        ids = [...new Set(list)];
        break;
      }
      default:
        return { ok: false, error: `Unknown setting "${key.slice(0, 30)}".` };
    }
  }
  if (source === 'picked') {
    if (ids.length === 0) return { ok: false, error: 'Pick at least one product.' };
    count = ids.length;
  } else if (ids.length > 0) {
    return { ok: false, error: 'ids only works with source=picked.' };
  }
  return {
    ok: true,
    feed: { source, count: count ?? DEFAULT_FEED_COUNT, columns, label, lang, ids },
  };
}

/** The directive text for a feed. Round-trips through {@link parseProductFeed}. */
export function productFeedDirective(feed: ProductFeed): string {
  const parts = [`source=${feed.source}`];
  if (feed.source === 'picked') parts.push(`ids=${feed.ids.join(',')}`);
  else parts.push(`count=${feed.count}`);
  parts.push(`columns=${feed.columns}`);
  if (feed.label !== DEFAULT_FEED_LABEL) parts.push(`label=${encodeLabel(feed.label)}`);
  if (feed.lang !== 'en') parts.push(`lang=${feed.lang}`);
  return `{{np_products ${parts.join(' ')}}}`;
}

/** Percent-encodes a label to the characters a directive allows. */
export function encodeLabel(label: string): string {
  return encodeURIComponent(label.trim()).replace(
    /[!'()*]/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`,
  );
}

/** Every feed in the mail that parses, in order. */
export function productFeedsOf(source: string): ProductFeed[] {
  if (!hasProductFeeds(source)) return [];
  const feeds: ProductFeed[] = [];
  for (const m of source.matchAll(DIRECTIVE)) {
    const parsed = parseProductFeed(m[1]!);
    if (parsed.ok) feeds.push(parsed.feed);
  }
  return feeds;
}

/** Save-time errors for a mail that uses product feeds wrongly (broken text, a bad setting, too many). */
export function checkProductFeedRefs(source: string): string[] {
  if (!hasProductFeeds(source)) return [];
  const errors: string[] = [];
  if (malformedProductFeedRefs(source) > 0) {
    errors.push('A product feed block is broken. Edit it in the editor again.');
  }
  let total = 0;
  for (const m of source.matchAll(DIRECTIVE)) {
    total += 1;
    const parsed = parseProductFeed(m[1]!);
    if (!parsed.ok) errors.push(`Product feed: ${parsed.error}`);
  }
  if (total > MAX_PRODUCT_FEEDS_PER_MAIL) {
    errors.push(`A mail can have at most ${MAX_PRODUCT_FEEDS_PER_MAIL} product feed blocks.`);
  }
  return errors;
}

/** Removes every directive (and any broken `{{np_products ...}}` text). The same string back without one. */
export function stripProductFeedRefs(source: string): string {
  return hasProductFeeds(source) ? source.replace(/\{\{np_products[^{}<]*(?:\}\})?/g, '') : source;
}

/** The products a feed shows, in order, from the whole catalogue. Out-of-stock products are left out. */
export function selectFeedProducts(
  feed: ProductFeed,
  catalog: readonly CatalogProduct[],
): CatalogProduct[] {
  const available = catalog.filter((p) => p.stockStatus !== 'outofstock');
  if (feed.source === 'picked') {
    const byId = new Map(available.map((p) => [p.id, p]));
    return feed.ids.flatMap((id) => byId.get(id) ?? []).slice(0, feed.count);
  }
  if (feed.source === 'best') {
    return available
      .filter((p) => p.soldRecent > 0)
      .sort((a, b) => b.soldRecent - a.soldRecent || a.shopRank - b.shopRank)
      .slice(0, feed.count);
  }
  return [...available].sort((a, b) => a.shopRank - b.shopRank).slice(0, feed.count);
}

function formatMoney(amount: string, currency: string, lang: string): string {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  try {
    return new Intl.NumberFormat(lang, { style: 'currency', currency }).format(n);
  } catch {
    return `${amount} ${currency}`;
  }
}

const HEX = /^#[0-9A-Fa-f]{6}$/;

/** Black or white, whichever reads on `background`. */
function onColor(background: string): string {
  const n = parseInt(background.slice(1), 16);
  const luma = 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255);
  return luma > 150 ? '#18181B' : '#FFFFFF';
}

function cardHtml(
  p: CatalogProduct,
  feed: ProductFeed,
  style: FeedStyle,
  widthPct: number,
  last: boolean,
): string {
  const onSale = p.salePrice !== '' && p.regularPrice !== '' && p.salePrice !== p.regularPrice;
  const current = formatMoney(onSale ? p.salePrice : p.price, p.currency, feed.lang);
  const regular = onSale ? formatMoney(p.regularPrice, p.currency, feed.lang) : '';
  const href = escapeHtml(p.permalink);
  const image = p.imageUrl
    ? `<a href="${href}"><img src="${escapeHtml(p.imageUrl)}" alt="${escapeHtml(p.name)}" width="160" style="display:block;width:100%;max-width:160px;height:auto;border:0;margin:0 auto 8px;"></a>`
    : '';
  const price = current
    ? `<div style="font-size:14px;color:${style.textColor};margin:0 0 10px;">${
        regular ? `<s style="color:#71717A;">${escapeHtml(regular)}</s> ` : ''
      }<strong>${escapeHtml(current)}</strong></div>`
    : '';
  const padding = last ? '0' : '0 8px 0 0';
  return `<td width="${widthPct}%" valign="top" align="center" style="padding:${padding};font-family:sans-serif;">${image}<div style="font-size:14px;line-height:1.3;color:${style.textColor};margin:0 0 4px;"><a href="${href}" style="color:${style.textColor};text-decoration:none;">${escapeHtml(p.name)}</a></div>${price}<a href="${href}" style="display:inline-block;background:${style.primaryColor};color:${onColor(style.primaryColor)};padding:8px 14px;border-radius:6px;text-decoration:none;font-size:13px;">${escapeHtml(feed.label)}</a></td>`;
}

/**
 * The HTML of one feed: a presentation table, cards side by side per row
 * (`columns`), inline styles only, like the other blocks. No products means
 * no HTML at all (an empty catalogue never leaves a hole or a broken table).
 */
export function renderProductFeed(
  feed: ProductFeed,
  catalog: readonly CatalogProduct[],
  style: FeedStyle = DEFAULT_FEED_STYLE,
): string {
  const products = selectFeedProducts(feed, catalog);
  if (products.length === 0) return '';
  const safe: FeedStyle = {
    primaryColor: HEX.test(style.primaryColor)
      ? style.primaryColor
      : DEFAULT_FEED_STYLE.primaryColor,
    textColor: HEX.test(style.textColor) ? style.textColor : DEFAULT_FEED_STYLE.textColor,
  };
  const columns = Math.min(feed.columns, products.length);
  const widthPct = Math.floor(100 / columns);
  const rows: string[] = [];
  for (let i = 0; i < products.length; i += columns) {
    const slice = products.slice(i, i + columns);
    const cells = slice.map((p, j) => cardHtml(p, feed, safe, widthPct, j === columns - 1));
    // A short last row keeps the same column width, so cards line up.
    for (let j = slice.length; j < columns; j++) {
      cells.push(`<td width="${widthPct}%" style="font-size:0;line-height:0;">&nbsp;</td>`);
    }
    rows.push(`<tr>${cells.join('')}</tr>`);
  }
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">${rows.join('<tr><td style="height:16px;line-height:16px;font-size:0;">&nbsp;</td></tr>')}</table>`;
}

export interface ProductFeedExpansion {
  readonly html: string;
  /** Feeds that rendered at least one product. */
  readonly rendered: number;
  /** Feeds with no product to show (empty catalogue, nothing in stock): replaced by nothing. */
  readonly empty: number;
}

/**
 * Replaces every directive with its product table. The same string comes back
 * when there is no directive. A directive with a bad setting is removed (the
 * save check refuses it earlier), so no `{{np_products}}` text can reach the
 * mail renderer.
 */
export function expandProductFeeds(
  source: string,
  catalog: readonly CatalogProduct[],
  style: FeedStyle = DEFAULT_FEED_STYLE,
): ProductFeedExpansion {
  if (!hasProductFeeds(source)) return { html: source, rendered: 0, empty: 0 };
  let rendered = 0;
  let empty = 0;
  const replaced = source.replace(DIRECTIVE, (_whole, settings: string) => {
    const parsed = parseProductFeed(settings);
    const html = parsed.ok ? renderProductFeed(parsed.feed, catalog, style) : '';
    if (html === '') empty += 1;
    else rendered += 1;
    return html;
  });
  return { html: stripProductFeedRefs(replaced), rendered, empty };
}
